import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

const defaultCompanySettings = {
  companyName: "BauHeld",
  tagline: "Bau & Renovierung",
  phone: "",
  email: "",
  website: "",
  address: "",
  city: "",
  postalCode: "",
  country: "Germany",
  vatNumber: "",
  taxNumber: "",
  iban: "",
  bic: "",
  bankName: "",
  accountHolder: "",
  invoicePrefix: "BH",
  invoiceNumberMode: "yearly",
  currency: "EUR",
  logoUrl: "",
  ownerUid: "",
  allowedUserUids: [],
  updatedAt: "",
};

const COMPANY_BRANDING_CACHE_KEY = "bauheld_company_branding";

function buildBrandingPayload(data = {}) {
  return {
    companyName: data.companyName || defaultCompanySettings.companyName,
    logoUrl: data.logoUrl || "",
    tagline: data.tagline || defaultCompanySettings.tagline,
  };
}

function writeBrandingCache(data = {}) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      COMPANY_BRANDING_CACHE_KEY,
      JSON.stringify(buildBrandingPayload(data))
    );
  } catch (error) {
    console.warn("Could not cache company branding:", error);
  }
}

function sanitizeAllowedUserUids(value, fallbackUid = "") {
  const items = Array.isArray(value) ? value : [];
  const normalized = items
    .map((item) => String(item || "").trim())
    .filter(Boolean);

  if (fallbackUid && !normalized.includes(fallbackUid)) {
    normalized.unshift(fallbackUid);
  }

  return Array.from(new Set(normalized));
}

function withAccessDefaults(data = {}, existing = {}) {
  const currentUid = auth.currentUser?.uid || "";
  const ownerUid =
    data.ownerUid ||
    existing.ownerUid ||
    currentUid ||
    "";

  return {
    ownerUid,
    allowedUserUids: sanitizeAllowedUserUids(
      data.allowedUserUids ?? existing.allowedUserUids,
      ownerUid
    ),
  };
}

export function getDefaultCompanySettings() {
  return { ...defaultCompanySettings };
}

export function getCachedCompanyBranding() {
  if (typeof window === "undefined") {
    return buildBrandingPayload(defaultCompanySettings);
  }

  try {
    const raw = window.localStorage.getItem(COMPANY_BRANDING_CACHE_KEY);
    if (!raw) return buildBrandingPayload(defaultCompanySettings);
    return buildBrandingPayload(JSON.parse(raw));
  } catch (error) {
    console.warn("Could not read company branding cache:", error);
    return buildBrandingPayload(defaultCompanySettings);
  }
}

export async function bootstrapCompanyOwnership() {
  const currentUid = auth.currentUser?.uid;
  if (!currentUid) return null;

  const ref = doc(db, "settings", "company");
  const snap = await getDoc(ref);

  if (snap.exists()) {
    const existing = { ...defaultCompanySettings, ...snap.data() };
    const accessPatch = withAccessDefaults({}, existing);

    if (
      existing.ownerUid !== accessPatch.ownerUid ||
      JSON.stringify(existing.allowedUserUids || []) !== JSON.stringify(accessPatch.allowedUserUids)
    ) {
      await setDoc(
        ref,
        {
          ...accessPatch,
          updatedAt: new Date().toISOString(),
          updatedAtServer: serverTimestamp(),
        },
        { merge: true }
      );
      return { ...existing, ...accessPatch };
    }

    return existing;
  }

  const payload = {
    ...defaultCompanySettings,
    ...withAccessDefaults(),
    updatedAt: new Date().toISOString(),
    updatedAtServer: serverTimestamp(),
  };

  await setDoc(ref, payload, { merge: false });
  writeBrandingCache(payload);
  return payload;
}

export async function getCompanySettings() {
  const ref = doc(db, "settings", "company");
  const snap = await getDoc(ref);

  if (snap.exists()) {
    const settings = { ...defaultCompanySettings, ...snap.data() };
    writeBrandingCache(settings);
    return settings;
  }

  return defaultCompanySettings;
}

export async function saveCompanySettings(data) {
  const ref = doc(db, "settings", "company");
  const snap = await getDoc(ref);
  const existing = snap.exists() ? snap.data() : {};
  const accessDefaults = withAccessDefaults(data, existing);

  const payload = {
    ...existing,
    ...data,
    ...accessDefaults,
    updatedAt: new Date().toISOString(),
    updatedAtServer: serverTimestamp(),
  };

  await setDoc(ref, payload, { merge: true });
  writeBrandingCache(payload);

  return payload;
}
